'use client'

import { IconDown } from 'icons/components/IconDown'
import { useEffect, useRef, useState } from 'react'
import { applyStylesIf, cx } from 'utils/cx'

import type { ElementProps } from '../Accordion.types'

import styles from './Element.module.scss'
import text from 'fukku/styles/texts.module.scss'

const SECONDARY_ICON_SIZE = 16

export const Element = ({
	id,
	title,
	subtitle,
	filled,
	secondary,
	children,
	onClick,
	dataTestId,
	onExpanded,
	onCollapsed,
	showExpanded = false,
	shouldScroll = true,
	shouldRenderOnExpand = false,
}: ElementProps): React.ReactNode => {
	const [expanded, setExpanded] = useState(showExpanded)
	const [currentSubtitle, setCurrentSubtitle] = useState(subtitle)
	const scrollRef = useRef<HTMLDivElement>(null)
	const firstRender = useRef(true)
	const iconSize = secondary ? SECONDARY_ICON_SIZE : undefined

	/**
	 * @returns {void}
	 */
	function handleClick() {
		setExpanded(!expanded)
	}

	/**
	 * update the expanded state when the prop changes
	 */
	useEffect(() => {
		setExpanded(showExpanded)
	}, [showExpanded])

	/**
	 * scrolling in view when enlarging and updating the subtitle
	 */
	useEffect(() => {
		if (expanded && shouldScroll && scrollRef.current) {
			scrollRef.current.scrollIntoView({ behavior: 'smooth' })
		}

		if (onClick && scrollRef.current) {
			if (firstRender.current) {
				onClick(expanded, scrollRef)
			}

			firstRender.current = true
		}
	}, [expanded])

	useEffect(() => {
		setCurrentSubtitle(subtitle)
	}, [subtitle])

	const handleTransitionEnd = () => {
		if (expanded) {
			onExpanded?.(id, setCurrentSubtitle)
		} else {
			onCollapsed?.(id, setCurrentSubtitle)
		}
	}

	return (
		<>
			<div ref={scrollRef} data-testid={dataTestId}>
				<button
					className={cx(text.titleM, styles.header)}
					type='button'
					aria-expanded={expanded}
					aria-controls={id}
					onClick={handleClick}
					id={`${id}-title`}
				>
					<div
						className={cx(
							styles.title,
							applyStylesIf(!!filled, styles.filled),
							applyStylesIf(!!secondary, styles.secondary),
							applyStylesIf(!!currentSubtitle, styles.hasSubtitle)
						)}
					>
						<p
							className={cx(
								styles.text,
								secondary || filled ? text.bodyM : text.bodyMRegular
							)}
						>
							{title}
						</p>
						<span
							className={cx(
								styles.icon,
								expanded ? styles.rotateIn : styles.rotateOut
							)}
						>
							<IconDown width={iconSize} height={iconSize} />
						</span>
					</div>
					{currentSubtitle && (
						<div className={cx(styles.subtitle, text.bodyS)}>
							{currentSubtitle}
						</div>
					)}
				</button>
			</div>
			<div
				// Workaround to make inert working in React
				// Reference https://github.com/facebook/react/issues/17157#issuecomment-1572230721
				{...{ inert: expanded ? null : '' }}
				id={id}
				className={cx(
					text.bodyM,
					styles.content,
					applyStylesIf(!!filled, styles.spacing),
					applyStylesIf(expanded, styles.show)
				)}
				data-testid={id}
				aria-labelledby={`${id}-title`}
				onTransitionEnd={handleTransitionEnd}
			>
				<div className={styles.child}>
					{shouldRenderOnExpand ? expanded && children : children}
				</div>
			</div>
		</>
	)
}
